
import React from "react";
import { ContentItem } from "@/types/course";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Pencil, Trash2, ArrowUp, ArrowDown, FileText } from "lucide-react";
import { getContentTypeIcon, getContentTypeName } from "./ContentTypeOptions";

interface ContentItemsListProps {
  contentItems: ContentItem[];
  onEditClick: (item: ContentItem) => void;
  onDeleteClick: (id: string) => void;
  onMoveClick: (id: string, direction: "up" | "down") => void;
  isLoading: boolean;
}

const ContentItemsList: React.FC<ContentItemsListProps> = ({
  contentItems,
  onEditClick,
  onDeleteClick,
  onMoveClick,
  isLoading
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <p>Loading content items...</p>
      </div>
    );
  }
  
  if (contentItems.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No content items found. Add your first content item to get started.</p>
      </div>
    );
  }
  
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[50px]">Order</TableHead>
          <TableHead className="w-[150px]">Type</TableHead>
          <TableHead>Title</TableHead>
          <TableHead className="w-[100px]">Required</TableHead>
          <TableHead className="w-[150px]">File</TableHead>
          <TableHead className="w-[200px]">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {contentItems.map((item) => {
          const Icon = getContentTypeIcon(item.content_type || "");
          const hasFile = item.file_path ? true : false;
          
          return (
            <TableRow key={item.id}>
              <TableCell className="font-medium">{(item.order_index || 0) + 1}</TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  <span>{getContentTypeName(item.content_type || "")}</span>
                </div>
              </TableCell>
              <TableCell>{item.title}</TableCell>
              <TableCell>{item.is_required ? "Yes" : "No"}</TableCell>
              <TableCell>
                {hasFile && (
                  <div className="flex items-center gap-2 text-sm text-techne-purple">
                    <FileText className="h-4 w-4" />
                    <span className="truncate max-w-[100px]" title={item.file_path}>
                      {item.file_path?.split('/').pop()}
                    </span>
                  </div>
                )}
              </TableCell>
              <TableCell>
                <div className="flex space-x-2">
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => onEditClick(item)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => onDeleteClick(item.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon"
                    disabled={contentItems.indexOf(item) === 0}
                    onClick={() => onMoveClick(item.id, "up")}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    size="icon"
                    disabled={contentItems.indexOf(item) === contentItems.length - 1}
                    onClick={() => onMoveClick(item.id, "down")}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default ContentItemsList;
